import classnames from 'classnames/bind';
import keycode from 'keycode';
import MuiCard from '@mui/material/Card';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import LoadBlur from 'components/LoadBlur';
import useIsMobile from 'lib/useIsMobile';

import styles from './index.scss';

const cx = classnames.bind(styles);
const defaultPosition = {
  x: 0,
  y: 0,
};
const filterKey = (e, cb) => {
  const key = keycode(e);
  if (key === 'enter' || key === 'space') {
    e.preventDefault();
    cb(e);
  }
};
const getTouchPosition = e => ({
  x: e.touches[0].clientX,
  y: e.touches[0].clientY,
});
const Card = ({ children, className, onClick: onClickRaw, transparent, ...props }) => {
  const mobile = useIsMobile();
  const touch = useRef(false);
  const touchMoved = useRef(false);
  const touchPosition = useRef(defaultPosition);
  const touchStarted = useRef(false);
  const [active, setActive] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const onBlur = useCallback(
    () => {
      setActive(false);
      setFocused(false);
    },
    [
      setActive,
      setFocused,
    ],
  );
  const onClick = useCallback(
    e => {
      if (!touch.current) {
        setActive(true);
        setLoading(true);
      }
      onClickRaw(e);
    },
    [
      onClickRaw,
      setActive,
      setLoading,
      touch,
    ],
  );
  const onFocus = useCallback(
    () => setFocused(true),
    [setFocused],
  );
  const onKeyDown = useCallback(
    e => filterKey(
      e,
      () => setActive(true),
    ),
    [setActive],
  );
  const onKeyUp = useCallback(
    e => filterKey(
      e,
      () => {
        setLoading(true);
        onClick(e);
      },
    ),
    [
      onClick,
      setLoading,
    ],
  );
  const onMouseDown = useCallback(
    () => {
      if (!touch.current) {
        setActive(true);
      }
    },
    [
      setActive,
      touch,
    ],
  );
  const onMouseUp = useCallback(
    () => {
      if (!touch.current) {
        setActive(false);
      }
    },
    [
      setActive,
      touch,
    ],
  );
  const onTouchCancel = useCallback(
    () => {
      setFocused(false);
      setActive(false);
      touchMoved.current = false;
      touchPosition.current = defaultPosition;
      touchStarted.current = false;
    },
    [
      setFocused,
      touchMoved,
      touchPosition,
      touchStarted,
    ],
  );
  const onTouchEnd = useCallback(
    e => {
      e.persist();
      touchStarted.current = false;
      if (!touchMoved.current) {
        setActive(true);
        setLoading(true);
        onClick(e);
      }
    },
    [
      onClick,
      setActive,
      touchMoved,
      touchStarted,
    ],
  );
  const onTouchMove = useCallback(
    e => {
      if (!touchMoved.current) {
        const { x: oldX, y: oldY } = touchPosition.current;
        const { x: newX, y: newY } = getTouchPosition(e);
        touchMoved.current = Math.abs(oldX - newX) > 10
          || Math.abs(oldY - newY) > 10;
      }
    },
    [
      touchMoved,
      touchPosition,
    ],
  );
  const onTouchStart = useCallback(
    e => {
      touch.current = true;
      if (!touchStarted.current) {
        touchStarted.current = true;
        touchMoved.current = false;
        touchPosition.current = getTouchPosition(e);
      }
    },
    [
      touch,
      touchMoved,
      touchPosition,
      touchStarted,
    ],
  );
  const card = useMemo(
    () => (
      <MuiCard
        {...props}
        className={cx({
          card: true,
          transparent: transparent || mobile,
          className,
        })}
      >
        {children}
      </MuiCard>
    ),
    [
      children,
      className,
      mobile,
      props,
      transparent,
    ],
  );
  return onClickRaw
    ? (
      <div
        className={`${styles.container} ${!mobile && focused
          ? styles.focused
          : ''}`}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onMouseDown={onMouseDown}
        onMouseEnter={onFocus}
        onMouseLeave={onBlur}
        onMouseUp={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchCancel={onTouchCancel}
        onTouchEnd={onTouchEnd}
        role='link'
        tabIndex='0'
      >
        <LoadBlur
          loading={active}
          showSpinner={loading}
        >
          {card}
        </LoadBlur>
      </div>
    )
    : card;
};

Card.propTypes = {
  ...MuiCard.propTypes,
  className: PropTypes.string,
  onClick: PropTypes.func,
  transparent: PropTypes.bool,
};

export default Card;
